import {
  Button,
  Checkbox,
  Menu as ChakraMenu,
  MenuButton,
  // MenuItem,
  MenuList,
  Text,
  Spacer,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Box,
  Input,
} from '@chakra-ui/react';
import { InputGroup, InputLeftAddon } from '@chakra-ui/react';
import { Tag, TagLabel } from '@chakra-ui/react';
import {
  TbDecimal,
  TbDropletMinus,
  TbMathXDivideY,
  TbMathXDivideY2,
} from 'react-icons/tb';
import { BiChevronDown, BiText } from 'react-icons/bi';
import { NumberFormat, NegativeNumFormatType } from '../../../../sheet/types';
import {
  defaultDateFormats,
  defaultDurationFormats,
  defaultFractionFormats,
  defaultTimeFormats,
  getVisibleNegativeFormat,
} from './formatData';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Menu, MenuItem, MenuProps } from '@szhsin/react-menu';
import { getCurrenySymbolFromCode } from '../../../../sheet/utils/numberFormat/generalHelpers';
import { allCurrencies, basicCurrencies } from '../../../data/currencies';
import { RiDropdownList, RiTimeLine } from 'react-icons/ri';
import CurrencyPicker from '../../pickers/CurrencyPicker';
import { BsCalendar2Date } from 'react-icons/bs';
import { CiTimer } from 'react-icons/ci';
import { RxLapTimer, RxTimer } from 'react-icons/rx';
import moment from 'moment';

// #region currency
const CurrencyIcon = ({ code = '' }: { code?: string }) => {
  let symbol = getCurrenySymbolFromCode(code);
  if (!symbol.length) return <RiDropdownList />;
  return <Text fontSize="14px">{symbol}</Text>;
};

const commonMenuProps: Partial<MenuProps> = {
  overflow: 'auto',
  portal: true,
  position: 'initial',
  menuStyle: {
    zIndex: 10000,
  },
  align: 'center',
  gap: 2,
};

const menuItemStyles: React.CSSProperties = {
  fontFamily: 'Lato',
  fontSize: '16px',
  textTransform: 'capitalize',
  textAlign: 'left',
  alignItems: 'center',
  justifyContent: 'center',
};

interface CurrencyProps {
  code: string;
  includeNone?: boolean;
  onChange: (key: string, value: any) => void;
}
export const CurrenciesMenu = ({
  code,
  includeNone,
  onChange,
}: CurrencyProps) => {
  const onSelect = (code: string) => {
    onChange('currencyCode', code);
  };
  let name = basicCurrencies[code] ?? allCurrencies[code];
  return (
    <Menu
      menuButton={
        <Button rounded="2xl" size="sm" fontWeight="medium" fontSize="14px">
          <CurrencyIcon code={code} /> {/* Left Icon */}
          <Spacer />
          <Text>{name ? name : 'Currency Symbol'}</Text>
          <Spacer />
          <BiChevronDown />
        </Button>
      }
      {...commonMenuProps}
      // overflow="auto"
      // position="initial"
      // portal={true}
      // // unmountOnClose={true}
      // menuStyle={{
      //   zIndex: 1000000,
      // }}
      // align="center"
      // gap={2}
    >
      <CurrencyPicker
        code={code}
        onSelect={onSelect}
        includeNone={includeNone}
      />
    </Menu>
  );
};
// #endregion

// #region negative number format picker
interface NegativeNumberFormatProps {
  value: NegativeNumFormatType;
  setFieldValue: (key: string, value: NegativeNumFormatType | string) => void;
}

const getTextColor = (negFormat: NegativeNumFormatType | string) => {
  return negFormat === NegativeNumFormatType.NegativeAsRed ||
    negFormat === NegativeNumFormatType.NegativeAsRedWithSign ||
    negFormat === NegativeNumFormatType.NegativeAsRedInBrackets
    ? 'red'
    : '';
};
export const NegativeNumberFormat = ({
  value,
  setFieldValue,
}: NegativeNumberFormatProps) => (
  <Menu
    menuButton={
      <Button
        rounded="2xl"
        size="sm"
        fontWeight="medium"
        fontSize="14px"
        color={getTextColor(value)}
        //   textAlign="left"
      >
        <TbDropletMinus />
        <Spacer />
        <Text>Negative Format {getVisibleNegativeFormat(value)}</Text>
        <Spacer />
        <BiChevronDown />
      </Button>
    }
    {...commonMenuProps}
  >
    <Box minWidth="200px">
      {Object.values(NegativeNumFormatType).map((negFormat) => (
        <MenuItem
          key={negFormat}
          type="checkbox"
          checked={value === negFormat}
          style={{
            ...menuItemStyles,
            color: getTextColor(negFormat),
          }}
          onClick={() => setFieldValue('negativeFormat', negFormat)}
        >
          {getVisibleNegativeFormat(negFormat)}
        </MenuItem>
      ))}
    </Box>
  </Menu>
);
// #endregion

// #region date Format
interface CommonValueWithSetProps {
  value: string;
  setFieldValue: (key: string, value: string) => void;
}
export const DateFormatMenu = ({
  value,
  setFieldValue,
}: CommonValueWithSetProps) => {
  return (
    <Menu
      menuButton={
        <Button rounded="2xl" size="sm" fontWeight="medium" fontSize="14px">
          <BsCalendar2Date />
          <Spacer />
          <Text>{value.length ? moment().format(value) : 'Date'}</Text>
          <Spacer />
          <BiChevronDown />
        </Button>
      }
      {...commonMenuProps}
    >
      <Box minWidth="200px">
        {['None', ...defaultDateFormats].map((code) => {
          const codeValue = code === 'None' ? '' : code;
          return (
            <MenuItem
              key={code}
              style={menuItemStyles}
              type="checkbox"
              checked={codeValue === value}
              onClick={() => setFieldValue('dateFormat', codeValue)}
            >
              {codeValue.length ? moment().format(code) : 'None'}
            </MenuItem>
          );
        })}
      </Box>
    </Menu>
  );
};
// #endregion

// #region time format
export const TimeFormatMenu = ({
  value,
  setFieldValue,
}: CommonValueWithSetProps) => {
  return (
    <Menu
      menuButton={
        <Button rounded="2xl" size="sm" fontWeight="medium" fontSize="14px">
          <RiTimeLine />
          <Spacer />
          <Text>{value.length ? moment().format(value) : 'Time'}</Text>
          <Spacer />
          <BiChevronDown />
        </Button>
      }
      {...commonMenuProps}
    >
      <Box minWidth="200px">
        {['None', ...defaultTimeFormats].map((code) => {
          const codeValue = code === 'None' ? '' : code;
          return (
            <MenuItem
              key={code}
              style={menuItemStyles}
              type="checkbox"
              checked={codeValue === value}
              onClick={() => setFieldValue('timeFormat', codeValue)}
            >
              {codeValue.length ? moment().format(code) : 'None'}
            </MenuItem>
          );
        })}
      </Box>
    </Menu>
  );
};
// #endregion

// #region duration format
export const DurationFormatMenu = ({
  value,
  setFieldValue,
}: CommonValueWithSetProps) => {
  return (
    <Menu
      menuButton={
        <Button rounded="2xl" size="sm" fontWeight="medium" fontSize="14px">
          <RxTimer />
          <Spacer />
          <Text>
            Duration {value.length ? `(${moment().format(value)})` : ''}
          </Text>
          <Spacer />
          <BiChevronDown />
        </Button>
      }
      {...commonMenuProps}
    >
      <Box minWidth="200px">
        {defaultDurationFormats.map((code) => (
          <MenuItem
            key={code}
            style={menuItemStyles}
            type="checkbox"
            checked={code === value}
            onClick={() => setFieldValue('durationFormat', code)}
          >
            {moment().format(code)}
          </MenuItem>
        ))}
      </Box>
    </Menu>
  );
};
// #endregion

// #region fraction format
export const FractionFormatMenu = ({
  value,
  setFieldValue,
}: CommonValueWithSetProps) => {
  return (
    <Menu
      menuButton={
        <Button rounded="2xl" size="sm" fontWeight="medium" fontSize="14px">
          <TbMathXDivideY2 />
          <Spacer />
          <Text>
            {defaultFractionFormats[value.split('?').length - 2]?.title ??
              'Fraction'}
          </Text>
          <Spacer />
          <BiChevronDown />
        </Button>
      }
      {...commonMenuProps}
    >
      <Box minWidth="200px">
        {defaultFractionFormats.map(({ title, code }) => (
          <MenuItem
            key={title}
            type="checkbox"
            checked={code === value}
            style={menuItemStyles}
            onClick={() => setFieldValue('fractionFormat', code)}
          >
            {title}
          </MenuItem>
        ))}
      </Box>
    </Menu>
  );
};
// #endregion

// #region Custom Input
export const CustomFormatInput = ({
  value,
  setFieldValue,
}: CommonValueWithSetProps) => (
  <InputGroup size="sm">
    <InputLeftAddon children={<BiText />} rounded="2xl" />
    <Input
      rounded="2xl"
      placeholder="Add and apply custom format"
      value={value}
      onChange={(e) => setFieldValue('customCode', e.target.value)}
    />
  </InputGroup>
);
// #endregion

// #region decimal places
interface DecimalPlacesProps {
  value: string | number;
  setFieldValue: (key: string, value: number | string) => void;
}
export const DecimalPlacesInput = ({
  value,
  setFieldValue,
}: DecimalPlacesProps) => {
  return (
    <InputGroup size="sm">
      <InputLeftAddon children={<TbDecimal />} rounded={'2xl'} />
      <NumberInput
        step={1}
        min={0}
        max={10}
        value={value}
        onChange={(valueString) =>
          setFieldValue('decimalPlaces', valueString.replace(/^\$/, ''))
        }
        focusInputOnChange={true}
        inputMode="numeric"
        width="100%"
      >
        <NumberInputField
          placeholder="Decimal Places"
          style={{
            borderTopRightRadius: '20px',
            borderBottomRightRadius: '20px',
            overflow: 'hidden',
          }}
        />
        <NumberInputStepper width="40px">
          <NumberIncrementStepper children={<FaChevronUp size="10px" />} />
          <NumberDecrementStepper children={<FaChevronDown size="10px" />} />
        </NumberInputStepper>
      </NumberInput>
    </InputGroup>
  );
};
// #endregion

// #region thousand separator
interface ThousandSepartorProps {
  value: boolean;
  setFieldValue: (key: string, value: boolean) => void;
}
export const ThousandSeparatorCheckbox = ({
  value,
  setFieldValue,
}: ThousandSepartorProps) => (
  <Tag
    size="lg"
    borderRadius="full"
    cursor="pointer"
    onClick={() => setFieldValue('showThousandSeparator', !value)}
  >
    <Checkbox
      isChecked={value}
      variant="circular"
      size="lg"
      pointerEvents="none"
      // onChange={(e) => setFieldValue('showThousandSeparator', e.target.checked)}
    >
      <TagLabel fontSize="14px" ml="3px">
        Thousand Separator
      </TagLabel>
    </Checkbox>
  </Tag>
);
// #endregion
